import { yupResolver } from "@hookform/resolvers/yup";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import { convertDateTime, formatDateTime } from "helpers/utils";
import { useTimeSlots } from "hooks";
import { useCreateHoliday, useUpdateHoliday } from "hooks/holiday/useHoliday";
import moment from "moment";
import { initOptionDateLocale } from "pages/ReportManagement/utils";
import React, { useEffect, useMemo, useState } from "react";
import Flatpickr from "react-flatpickr";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useToasts } from "react-toast-notifications";
import { Button, Col, Form, FormGroup, Label, Row } from "reactstrap";
import * as yup from "yup";
import MessageFormValidate from "../../components/shared-components/MessageFormValidate";
import {
  formConvertErrorMessage,
  formValidate,
} from "../../helpers/form_helper";
import { Switch } from "antd";

const getTimeHoliday = (listOption, date) => {
  const time = date.split(/\s+/)[1];

  const item = listOption.find(e => e.value === time);

  return item;
};

const HolidayForm = ({ holiday, onSuccess, ...props }) => {
  const { t } = useTranslation(["holiday"]);
  const [timeSlotParams, setTimeSlotParams] = useState({
    page: 1,
    "per-page": 20,
    "filter[is_even_shift]": 1,
  });

  const { data: timeSlotData, refetch: fetchTimeSlot } = useTimeSlots({
    key: timeSlotParams["filter[hub_id]"],
    params: timeSlotParams,
  });

  const optionsStartTime = useMemo(() => {
    if (timeSlotData) {
      return timeSlotData?.items?.map(item => ({
        label: t("form.time.start", {
          target: `${item.name} (${item.from_time})`,
        }),
        value: item.from_time,
      }));
    }
    return [];
  }, [timeSlotData, t]);

  const optionsEndTime = useMemo(() => {
    if (timeSlotData) {
      return timeSlotData?.items?.map(item => ({
        label: t("form.time.end", {
          target: `${item.name} (${item.to_time})`,
        }),
        value: item.to_time,
      }));
    }
    return [];
  }, [timeSlotData, t]);

  const initDefaultValues = {
    hub_id: null,
    from_date: holiday?.from_date ? [new Date(holiday?.from_date)] : [],
    to_date: holiday?.to_date ? [new Date(holiday?.to_date)] : [],
    name: holiday?.name || "",
    start_time: null,
    end_time: null,
  };

  useEffect(() => {
    if (holiday) {
      if (optionsStartTime && optionsStartTime.length > 0) {
        setValue(
          "start_time",
          getTimeHoliday(optionsStartTime, holiday?.from_date)
        );
      }

      if (optionsEndTime && optionsEndTime.length > 0) {
        setValue("end_time", getTimeHoliday(optionsEndTime, holiday?.to_date));
      }
    }
  }, [holiday, optionsStartTime, optionsEndTime]);

  const onHolidaySuccess = data => {
    onSuccess?.(data.data);
  };
  const { addToast } = useToasts();
  const onErrorHoliday = error => {
    if (error.from_date === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
  };
  const { mutate: createHoliday, isLoading: isLoadingCreate } =
    useCreateHoliday(onHolidaySuccess, onErrorHoliday);
  const { mutate: updateHoliday, isLoading: isLoadingUpdate } =
    useUpdateHoliday(onHolidaySuccess, onErrorHoliday);

  const validator = {
    hub_id: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: t("form.hub_id") })),
    name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.name") }))
      .max(255, t("form.validate.char_max", { max: 255 })),
    from_date: yup.array().min(
      1,
      t("form.validate.required", {
        title: t("form.from_date"),
      })
    ),
    start_time: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: t("form.start_time") })),
    to_date: yup
      .array()
      .min(
        1,
        t("form.validate.required", {
          title: t("form.to_date"),
        })
      )
      .test("validToDate", t("form.validate.from_date_fail"), (val, data) => {
        const fromDateData = data?.parent?.from_date || null; //
        if (fromDateData?.length === 0 || val?.[0] >= fromDateData?.[0]) {
          return true;
        }
        return false;
      }),
    end_time: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: t("form.end_time") }))
      .test("validEndTime", t("form.validate.end_time_fail"), (val, data) => {
        const startTime = moment(
          data?.parent?.start_time?.value || "",
          "HH:mm"
        );
        const endTime = moment(val?.value || "", "HH:mm");
        const fromDate = moment(data?.parent?.from_date[0]).startOf("day");
        const toDate = moment(data?.parent?.to_date[0]).startOf("day");

        if (fromDate.isSame(toDate) && endTime.isBefore(startTime)) {
          return false;
        }

        return true;
      }),
  };
  const holidayValidationSchema = yup.object().shape(validator);

  const mainForm = useForm({
    resolver: yupResolver(holidayValidationSchema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: initDefaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting, isDirty },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
    watch,
  } = mainForm;

  const watchedFromDate = watch("from_date");
  const watchedToDate = watch("to_date");
  const watchedHubId = watch("hub_id");

  useEffect(() => {
    // Trigger validation for to_date whenever from_date changes
    // if (watchedFromDate?.length > 0 || watchedToDate?.length > 0) {

    if (
      (watchedFromDate?.length > 0 || watchedToDate?.length > 0) &&
      (!holiday || getValues("end_time"))
    ) {
      trigger(["to_date", "end_time"]);
    }
  }, [watchedFromDate, watchedToDate]);

  const handleSubmitForm = () => {
    trigger();
    handleSubmit(() => {
      const data = getValues();

      const fromDate = data.from_date[0];
      const fromDateFinal = `${convertDateTime(
        fromDate,
        formatDateTime.YYYY_MM_DD,
        "-"
      )} ${data.start_time.value}`;

      // const toDate = new Date(data.to_date[0].setHours(23, 59, 0, 0));
      const toDate = data.to_date[0];

      const toDateFinal = `${convertDateTime(
        toDate,
        formatDateTime.YYYY_MM_DD,
        "-"
      )} ${data.end_time.value}`;
      const finalData = {
        ...data,
        hub_id: data?.hub_id?.value,
        from_date: fromDateFinal,
        to_date: toDateFinal,
      };
      if (holiday) {
        finalData.holidayId = holiday.id;
        updateHoliday(finalData);
      } else {
        createHoliday(finalData);
      }
    })();
  };

  useEffect(() => {
    if (watchedHubId) {
      setTimeSlotParams(prev => ({
        ...prev,
        "filter[hub_id]": watchedHubId.value,
      }));
    }
  }, [watchedHubId]);
  useEffect(() => {
    if (watchedHubId) {
      fetchTimeSlot();
    }
  }, [watchedHubId, timeSlotParams]);

  // const [is_level_2_modal, setIsLevel2Modal] = useState(true);
  // const handleChangeIsLevel2Modal = value => {
  //   setIsLevel2Modal(value);
  //   setValue("hub_id", null);
  // };

  return (
    <Form className="p-4" onSubmit={handleSubmitForm}>
      <Row>
        {/* <div
          className="col-12 col-md-12 mb-3"
          style={{ display: "flex", alignItems: "center" }}
        >
          <Label
            className="d-flex align-items-center gap-1 mb-0"
            style={{ cursor: "pointer" }}
            htmlFor="is_level_2"
            onClick={() => handleChangeIsLevel2Modal(!is_level_2_modal)}
          >
            <Switch
              checked={is_level_2_modal}
              onChange={handleChangeIsLevel2Modal}
            />
            Địa chỉ 2 cấp
          </Label>
        </div> */}
        <Col xs={6}>
          <FormHubSelect
            isMyHubOnly
            hubIdDefault={holiday?.hub_id}
            isRequired
            mainForm={mainForm}
            fieldName="hub_id"
            t={t}
            title={t("form.hub_id")}
            addressLevel={3}
          />
        </Col>
        <Col xs={6}>
          <FormGroup>
            <Label htmlFor="name">
              {t("form.name")} <span className="text-danger">*</span>
            </Label>
            <input
              name="name"
              type="text"
              className={`form-control ${formValidate(
                errors,
                touchedFields,
                "name"
              )}`}
              placeholder={t("placeholder.enter", {
                title: t("form.name"),
              })}
              {...register("name")}
            />
            {errors.name && <MessageFormValidate error={errors.name.message} />}
          </FormGroup>
        </Col>
        <Col xs={8}>
          <FormGroup>
            <Label htmlFor="from_date">
              {t("form.from_date")} <span className="text-danger">*</span>
            </Label>
            <Controller
              control={control}
              name="from_date"
              render={({ field }) => (
                <Flatpickr
                  {...field}
                  className="form-control d-block Flatpickr__Input"
                  placeholder={t("form.from_date")}
                  options={{
                    ...initOptionDateLocale,
                    altFormat: "d/m/Y",
                    dateFormat: "d/m/Y",
                  }}
                  t
                />
              )}
            />
            {errors.from_date && (
              <MessageFormValidate error={errors.from_date.message} />
            )}
          </FormGroup>
        </Col>
        <Col xs={4}>
          <FormGroup>
            <Label htmlFor="start_time">
              {t("form.start_time")} <span className="text-danger">*</span>
            </Label>
            <Controller
              control={control}
              name="start_time"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: t("form.start_time"),
                  })}
                  isDisabled={!watchedHubId ? true : false}
                  options={optionsStartTime}
                  isSearchable
                  className="basic-single w-100"
                  classNamePrefix="select"
                />
              )}
            />
            {errors.start_time && (
              <MessageFormValidate error={errors.start_time.message} />
            )}
          </FormGroup>
        </Col>
        <Col xs={8}>
          <FormGroup>
            <Label htmlFor="to_date">
              {t("form.to_date")} <span className="text-danger">*</span>
            </Label>
            <Controller
              control={control}
              name="to_date"
              render={({ field }) => (
                <Flatpickr
                  {...field}
                  className="form-control d-block Flatpickr__Input"
                  placeholder={t("form.to_date")}
                  options={{
                    ...initOptionDateLocale,
                    altFormat: "d/m/Y",
                    dateFormat: "d/m/Y",
                  }}
                />
              )}
            />
            {errors.to_date && (
              <MessageFormValidate error={errors.to_date.message} />
            )}
          </FormGroup>
        </Col>
        <Col xs={4}>
          <FormGroup>
            <Label htmlFor="end_time">
              {t("form.end_time")} <span className="text-danger">*</span>
            </Label>
            <Controller
              control={control}
              name="end_time"
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("placeholder.select", {
                    title: t("form.end_time"),
                  })}
                  isDisabled={!watchedHubId ? true : false}
                  options={optionsEndTime}
                  isSearchable
                  className="basic-single w-100"
                  classNamePrefix="select"
                />
              )}
            />
            {errors.end_time && (
              <MessageFormValidate error={errors.end_time.message} />
            )}
          </FormGroup>
        </Col>
        <Col className="text-end">
          <Button
            size="xs"
            color="primary"
            disabled={isLoadingCreate || isLoadingUpdate}
            style={{ backgroundColor: "#3C64B1" }}
            onClick={handleSubmitForm}
          >
            {holiday ? t("button.update") : t("button.add")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default HolidayForm;
